import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, Lock as LockIcon } from "lucide-react";
import Logo from "@/components/Logo";
import { useSiteSettings } from "@/lib/SiteSettingsContext";
import api from "@/lib/api";

const DEFAULTS = {
  topbar_left1: "ISO 9001 · ISO 14001 Sertifikalı Üretim",
  topbar_left2: "15+ Ülkeden Direkt İthalat",
  topbar_admin_label: "Yönetici Girişi",
  nav_home: "Anasayfa",
  nav_about: "Hakkımızda",
  nav_products: "Ürünler",
  nav_sectors: "Sektörler",
  nav_contact: "İletişim",
  cta_label: "Bize Ulaşın",
};

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hc, setHc] = useState(DEFAULTS);
  const location = useLocation();
  const { settings } = useSiteSettings();

  useEffect(() => {
    api.get("/page-content/header").then(({ data }) => setHc({ ...DEFAULTS, ...data })).catch(() => {});
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const NAV = [
    { to: "/", label: hc.nav_home },
    { to: "/hakkimizda", label: hc.nav_about },
    { to: "/urunler", label: hc.nav_products },
    { to: "/sektorler", label: hc.nav_sectors },
    { to: "/iletisim", label: hc.nav_contact },
  ];

  return (
    <>
      {/* Top bar */}
      <div className="hidden md:block bg-[#0A2540] text-white text-xs" data-testid="top-bar">
        <div className="max-w-7xl mx-auto px-4 lg:px-12 py-2 flex justify-between items-center">
          <div className="flex items-center gap-6 text-white/70">
            {hc.topbar_left1 && <span>{hc.topbar_left1}</span>}
            {hc.topbar_left1 && hc.topbar_left2 && <span>·</span>}
            {hc.topbar_left2 && <span>{hc.topbar_left2}</span>}
          </div>
          <div className="flex items-center gap-4">
            <a
              href={`tel:${settings.phone?.replace(/\s/g, "")}`}
              className="flex items-center gap-2 hover:text-[#0066FF] transition-colors"
              data-testid="topbar-phone-link"
            >
              <Phone size={12} /> {settings.phone}
            </a>
            <span className="text-white/30">·</span>
            <Link
              to="/admin/login"
              className="flex items-center gap-1.5 hover:text-[#0066FF] transition-colors"
              data-testid="topbar-admin-link"
            >
              <LockIcon size={11} /> {hc.topbar_admin_label}
            </Link>
          </div>
        </div>
      </div>

      <header
        className={`sticky top-0 z-50 transition-all border-b ${
          scrolled
            ? "bg-white/85 backdrop-blur-xl border-slate-200"
            : "bg-white border-transparent"
        }`}
        data-testid="main-header"
      >
        <div className="max-w-7xl mx-auto px-4 lg:px-12">
          <div className="flex items-center justify-between h-20 lg:h-24">
            <Link to="/" data-testid="header-logo-link">
              <Logo size="md" />
            </Link>

            <nav className="hidden lg:flex items-center gap-1">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  data-testid={`nav-${n.to.replace(/\//g, "") || "home"}`}
                  className={({ isActive }) =>
                    `px-4 py-2 text-sm font-medium transition-colors ${
                      isActive
                        ? "text-[#0066FF]"
                        : "text-[#0A2540]/80 hover:text-[#0066FF]"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
            </nav>

            <div className="flex items-center gap-3">
              <Link
                to="/iletisim"
                className="hidden md:inline-flex items-center gap-2 bg-[#0A2540] hover:bg-[#0066FF] text-white px-5 py-2.5 text-sm font-semibold transition-colors"
                data-testid="header-cta-contact"
              >
                {hc.cta_label}
              </Link>
              <button
                className="lg:hidden p-2 text-[#0A2540]"
                onClick={() => setOpen(!open)}
                aria-label="Menu"
                data-testid="header-mobile-toggle"
              >
                {open ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {open && (
          <div className="lg:hidden border-t bg-white max-h-[calc(100vh-6rem)] overflow-y-auto" data-testid="mobile-menu">
            <nav className="flex flex-col p-4 gap-1">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  data-testid={`mobile-nav-${n.to.replace(/\//g, "") || "home"}`}
                  className={({ isActive }) =>
                    `px-4 py-3 text-sm font-medium ${
                      isActive
                        ? "bg-[#0066FF]/10 text-[#0066FF]"
                        : "text-[#0A2540] hover:bg-slate-50"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              <Link
                to="/iletisim"
                className="mt-2 bg-[#0A2540] text-white px-4 py-3 text-sm font-semibold text-center"
                data-testid="mobile-cta-contact"
              >
                {hc.cta_label}
              </Link>

              {/* Mobile-only contact + admin links */}
              <div className="mt-3 pt-4 border-t border-slate-100 space-y-1">
                {settings.phone && (
                  <a
                    href={`tel:${settings.phone.replace(/\s/g, "")}`}
                    className="flex items-center gap-3 px-4 py-3 text-sm text-[#0A2540]/80 hover:bg-slate-50"
                    data-testid="mobile-phone-link"
                  >
                    <Phone size={14} className="text-[#0066FF]" />
                    {settings.phone}
                  </a>
                )}
                <Link
                  to="/admin/login"
                  className="flex items-center gap-3 px-4 py-3 text-sm text-[#0A2540]/80 hover:bg-slate-50"
                  data-testid="mobile-admin-link"
                >
                  <LockIcon size={13} className="text-[#0066FF]" />
                  {hc.topbar_admin_label}
                </Link>
              </div>
            </nav>
          </div>
        )}
      </header>
    </>
  );
}
